import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import ForgotPasswordModal from '../components/ForgotPasswordModal.tsx';
import { supabase } from '../services/supabaseClient.ts';
import { handlePostLoginRedirect } from '../services/authRedirect.ts';

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Show success message from password reset if available
  const resetMessage = location.state?.message;

  const isEmailValid = email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  const isFormValid = isEmailValid && password.length > 0;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!isFormValid) {
      setError('Please enter a valid email and password.');
      return;
    }
    setLoading(true);
    try {
      console.log('[LoginScreen] Attempting login for:', email);
      
      const { data, error: supaError } = await supabase.auth.signInWithPassword({ email, password });
      if (supaError) {
        console.error('[LoginScreen] Login failed:', supaError.message);
        
        // Handle specific error cases with better user guidance
        if (supaError.message.includes('Invalid login credentials') || 
            supaError.message.includes('Email not confirmed')) {
          if (supaError.message.includes('Email not confirmed')) {
            setError('Please check your email and click the confirmation link before logging in.');
          } else {
            setError('Invalid email or password. Don\'t have an account yet?');
          }
        } else if (supaError.message.includes('Email not found') || 
                   supaError.message.includes('User not found')) {
          setError('No account found with this email. Would you like to sign up instead?');
        } else {
          setError(supaError.message);
        }
        setLoading(false);
        return;
      }
      console.log('[LoginScreen] Login success:', data);
      await handlePostLoginRedirect();
    } catch (err: any) {
      setError(err.message || 'Login failed.');
      console.error('[LoginScreen] Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans flex flex-col">
      <Navbar />
      <main className="container mx-auto px-6 py-8 flex-1">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow p-8">
          <h1 className="text-2xl font-bold mb-6 text-center flex items-center gap-2 justify-center"><Lock className="w-6 h-6" /> Login</h1>
          
          {resetMessage && (
            <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-green-600 text-sm">{resetMessage}</p>
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700">Email address</label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Email address"
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Password"
                value={password}
                onChange={handlePasswordChange}
                disabled={loading}
              />
            </div>
            {error && <div className="text-red-500 text-sm" role="alert">{error}</div>}
            <button
              type="submit"
              className="w-full py-2 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={loading || !isFormValid}
            >
              {loading ? 'Logging in…' : 'Login'}
            </button>
          </form>
          <div className="mt-6 flex flex-col gap-2 text-center text-sm">
            <Link to="/signup" className="text-blue-600 hover:underline">Don't have an account? Sign up</Link>
            <Link to="/upload" className="text-gray-500 hover:underline">Back to Upload</Link>
          </div>
        </div>
      </main>
      <Footer />
      
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </div>
  );
} 